import { types, flow } from "mobx-state-tree";

import * as OrganizationAPI from "api/organizations";

import BaseBackboneModelMigrationStore from "pages/common/state/store/backbone-model-template";

import OrganizationCredentialStore from "./organization-credential-store";

const model = {
    organizationId: types.identifier,
    name: types.maybe(types.string),
    defaultFacebookUserId: types.maybe(types.string),
    defaultLocale: types.maybe(types.string),
    defaultTwitterUser: types.maybe(types.string),
    isMfaEnabled: types.optional(types.boolean, false, [undefined, null]),
    isCognitoOrganization: types.maybe(types.boolean),
    samlConfigured: types.maybe(types.boolean),
    maxNumberOfUsers: types.maybe(types.integer),
    organizationCredentials: types.optional(
        types.array(OrganizationCredentialStore),
        []
    )
};

const volatile = () => ({
    identifierAttribute: `organizationId`
});

const views = (self) => ({
    get id() {
        return self.organizationId;
    },
    isMaxNumberOfUsersReached(numOfUsers) {
        if (self.maxNumberOfUsers) {
            return numOfUsers >= self.maxNumberOfUsers;
        }
        return false;
    },
    getOrganizationCredentialById(organizationCredentialId) {
        return self.organizationCredentials?.find(
            (credential) =>
                credential.organizationCredentialId === organizationCredentialId
        );
    },
    getOrganizationCredentialsByAllowedUserId(userId) {
        return self.organizationCredentials?.filter((credential) =>
            credential.isAssignedToUser(userId)
        );
    }
});

const actions = (self) => ({
    toggleMfaEnabled() {
        self.isMfaEnabled = !self.isMfaEnabled;
    },
    setIsCognitoOrganization(state) {
        self.isCognitoOrganization = state;
    }
});

const asyncActions = (self) => ({
    /**
     * @summary Make an api call to fetch all organization credentials
     * @async
     * @return {Array<OrganizationCredentials> | Error}
     */
    fetchOrganizationCredentials: flow(
        function* fetchOrganizationCredentials() {
            try {
                const data =
                    yield OrganizationAPI.getAllOrganizationCredentials();
                self.organizationCredentials = data;
                return self.organizationCredentials;
            } catch (error) {
                console.error(
                    `ERROR FETCHING ORGANIZATION CREDENTIALS.`,
                    error
                );
                return error;
            }
        }
    )
});

const _OrganizationStore = types
    .model(`Organization`, model)
    .volatile(volatile)
    .actions(actions)
    .actions(asyncActions)
    .views(views);

export const OrganizationStore =
    BaseBackboneModelMigrationStore<typeof _OrganizationStore>(
        _OrganizationStore
    );
